/**
 * "My office network" (CU-869fmjzrh D10): the server hands back, per office and column, each provider's (or site's)
 * stats in both periods; this composes every figure of the screen — your price, the market median of the competitors
 * shown, your distance in % and €, its change, your position, the cheapest and the dearest, the coverage and the reason
 * of an empty cell. Min / Avg / Max, Total / Per day and Tier / Providers never ask the server again.
 *
 * - **Brand** ("Me as" my brand): You = the primary brand; your other brands are `Yours`, out of the median and of the
 *   position (Season decision 3). Tier / Providers choose the competitors that count; You always counts.
 * - **Website**: You = your site whose country is the office's (CU-869fmj51h; with several, the cheapest of them, the
 *   others `Yours`). No site of yours in that country → the row says so.
 * - **No identity** (or a broker, CA 21): each cell is the market median of everyone shown, and its change.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { isPrice, median, rankOf } from '../../../@services/stats.js'

export const THIN_PRICES = 5
export const MIN_COMPETITORS = 2
const STAT_INDEX = { min: 1, avg: 2, max: 3 }

/** `[{ id, v, n }]` from the compact rows `[idx, min, avg, max, n]` for a stat; per day divides by the length */
export function entriesOf(list, who, stat = 'min', days = null) {
	const i = STAT_INDEX[stat] || 1
	return (list || []).map(r => {
		const w = who[r[0]]
		let v = r[i]
		if (isPrice(v) && days) v = v / days
		return w ? { id: w.id, name: w.name, own: w.own || null, market: w.market || null, v: isPrice(v) ? v : null, n: r[4] || 0 } : null
	}).filter(Boolean)
}

/** Who is You, who is yours, who competes, in one office (D7) */
export function rolesFor({ identity, meAs, office, visible = null }) {
	if (!identity) return { mode: 'market', youIds: [], ownIds: new Set(), visible }
	if (meAs === 'site') {
		const country = String(office?.country || '').toUpperCase()
		const youIds = (identity.siteIds || []).filter(id => (identity.siteMarkets || {})[String(id)] === country && country)
		const ownIds = new Set((identity.siteIds || []).map(Number))
		return { mode: 'site', youIds: youIds.map(Number), ownIds, visible: null }
	}
	return { mode: 'brand', youIds: [Number(identity.primaryId)], ownIds: new Set([Number(identity.primaryId), ...(identity.otherIds || []).map(Number)]), visible }
}

function split(entries, roles) {
	const priced = entries.filter(e => isPrice(e.v))
	const shown = e => !roles.visible || roles.visible.has(Number(e.id))
	if (roles.mode === 'market') {
		const market = priced.filter(shown)
		return { you: null, yours: [], competitors: market, all: market }
	}
	const mine = priced.filter(e => roles.youIds.includes(Number(e.id))).sort((a, b) => a.v - b.v)
	const you = mine[0] || null
	const yours = priced.filter(e => roles.ownIds.has(Number(e.id)) && e !== you)
	const competitors = priced.filter(e => !roles.ownIds.has(Number(e.id)) && shown(e))
	const all = [...(you ? [you] : []), ...yours.filter(shown), ...competitors]
	return { you, yours, competitors, all }
}

function periodOf(entries, roles) {
	const { you, yours, competitors, all } = split(entries, roles)
	const med = competitors.length >= MIN_COMPETITORS ? median(competitors.map(e => e.v)) : null
	const distance = you && isPrice(med) && med ? (you.v - med) / med : null
	const n = all.reduce((s, e) => s + (e.n || 0), 0)
	return { you, yours, competitors, all, median: med, distance, n }
}

/**
 * One cell for the chosen stat: `{ empty, reason, you, median, nCompetitors, distance, distanceE, prevDistance,
 * change (points), marketChange, position, total, cheapest, dearest, thin, list, prevList }`.
 */
export function cellOf(raw, who, { roles, stat = 'min', days = null } = {}) {
	const cur = periodOf(entriesOf(raw?.cur, who, stat, days), roles)
	const prev = raw?.prev ? periodOf(entriesOf(raw.prev, who, stat, days), roles) : null

	const cell = {
		empty: !cur.all.length && !cur.you, reason: null, why: raw?.why || null,
		you: cur.you, median: cur.median, nCompetitors: cur.competitors.length,
		distance: cur.distance, distanceE: isPrice(cur.distance) ? cur.you.v - cur.median : null,
		prevMedian: prev ? prev.median : null, prevDistance: prev ? prev.distance : null,
		change: null, marketChange: null, position: null, total: cur.competitors.length + (cur.you ? 1 : 0),
		cheapest: null, dearest: null, thin: false, n: cur.n, prevN: prev ? prev.n : null,
		list: [...cur.all].sort((a, b) => a.v - b.v || String(a.name).localeCompare(String(b.name))),
		yours: cur.yours,
	}

	if (!raw?.cur?.length) cell.reason = 'noPrices'
	else if (roles.mode !== 'market' && !cur.you) cell.reason = 'noYou'
	else if (cur.competitors.length < MIN_COMPETITORS) cell.reason = 'fewCompetitors'

	if (isPrice(cell.distance) && prev && isPrice(prev.distance)) cell.change = (cell.distance - prev.distance) * 100
	if (isPrice(cur.median) && prev && isPrice(prev.median) && prev.median) cell.marketChange = (cur.median - prev.median) / prev.median
	if (cur.you) cell.position = rankOf(cur.competitors.map(e => e.v), cur.you.v)
	if (cell.list.length) {
		cell.cheapest = cell.list[0]
		cell.dearest = cell.list[cell.list.length - 1]
	}
	cell.thin = !cell.reason && cur.n < THIN_PRICES
	return cell
}

/** The number of days a column's prices are divided by when "Per day" is on */
const daysOf = (column, lor) => column?.lor || lor || null

/**
 * The table of the lengths or the pick-up view: one row per office (all of them, CA 9), one cell per column.
 * `visible` = the provider ids Tier / Providers leave (a Set), `null` = everyone.
 */
export function buildNetworkModel(data, { stat = 'min', perDay = false, meAs = 'brand', visible = null, lor = null } = {}) {
	const identity = data?.identity || null
	const who = data?.who || []
	const columns = data?.columns || []
	const rows = (data?.offices || []).map(office => {
		const roles = rolesFor({ identity, meAs, office, visible })
		const byColumn = data?.cells?.[String(office.id)] || {}
		const cells = columns.map(c => cellOf(byColumn[c.key], who, { roles, stat, days: perDay ? daysOf(c, lor) : null }))
		let reason = null
		if (!office.captured) reason = 'notCaptured'
		else if (roles.mode === 'site' && !roles.youIds.length) reason = 'noWebsite'
		return { office, cells, reason }
	})
	const mode = identity ? (meAs === 'site' ? 'site' : 'brand') : 'market'
	const thinCells = rows.reduce((s, r) => s + r.cells.filter(c => c.thin).length, 0)
	return { columns, rows, identity, mode, filtered: !!visible, thinCells, notCaptured: rows.filter(r => r.reason === 'notCaptured').length }
}

/**
 * The provider (or website) view of one length (CA 15): one row per office, one column per provider of the answer,
 * each cell that provider's distance to the office's median; You and the median are their own columns.
 */
export function buildProviderModel(data, { stat = 'min', perDay = false, meAs = 'brand', visible = null, lor = null, columnKey = null } = {}) {
	const identity = data?.identity || null
	const who = data?.who || []
	const key = columnKey ?? String(lor)
	const rolesFirst = rolesFor({ identity, meAs, office: null, visible })
	const shownWho = who.filter(w => (rolesFirst.mode === 'brand' ? !rolesFirst.ownIds.has(Number(w.id)) : !(w.own === 'site' && rolesFirst.mode === 'site')) && (!visible || visible.has(Number(w.id))))
	const rows = (data?.offices || []).map(office => {
		const roles = rolesFor({ identity, meAs, office, visible })
		const cell = cellOf(data?.cells?.[String(office.id)]?.[key], who, { roles, stat, days: perDay ? lor : null })
		const byId = new Map(cell.list.map(e => [Number(e.id), e]))
		const providers = shownWho.map(w => {
			const e = byId.get(Number(w.id))
			const d = e && isPrice(cell.median) && cell.median ? (e.v - cell.median) / cell.median : null
			return { id: w.id, value: e ? e.v : null, distance: d, cheapest: !!e && cell.cheapest && Number(cell.cheapest.id) === Number(w.id) }
		})
		let reason = null
		if (!office.captured) reason = 'notCaptured'
		else if (roles.mode === 'site' && !roles.youIds.length) reason = 'noWebsite'
		return { office, cell, providers, reason }
	})
	return { providers: shownWho, rows, identity, mode: identity ? (meAs === 'site' ? 'site' : 'brand') : 'market' }
}
